import type { FC, ReactNode } from 'react'
import type { NavCollection } from '@/types'

import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { CalendarDays, Menu, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { useBag } from '@/components/generals/bag-provider'
import { MenuDrawer } from '@/components/generals/menu-drawer'
import { Wordmark } from '@/components/generals/wordmark'
import { maisonLinks } from '@/constants/site'

interface HeaderProps {
    navigation: Array<NavCollection>
}

export const Header: FC<HeaderProps> = ({ navigation }): ReactNode => {
    const router = useRouter()
    const { count, ready } = useBag()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const openMenu = useCallback((): void => setMenuOpen(true), [])
    const closeMenu = useCallback((): void => setMenuOpen(false), [])

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const activeSlug = router.pathname === '/collections/[slug]' && typeof router.query.slug === 'string' ? router.query.slug : null
    const bagLabel = ready && count > 0 ? `Bag, ${count} ${count === 1 ? 'item' : 'items'}` : 'Bag'

    return (
        <>
            <a
                href='#main'
                className='sr-only z-[60] bg-foreground px-4 py-3 text-sm text-background focus:not-sr-only focus:fixed focus:left-4 focus:top-4'
            >
                Skip to content
            </a>

            <header
                className={cn(
                    'sticky top-0 z-40 border-b bg-background/95 backdrop-blur-sm transition-colors duration-500',
                    scrolled ? 'border-border' : 'border-transparent'
                )}
            >
                <div className='mx-auto grid h-16 max-w-[1600px] grid-cols-[1fr_auto_1fr] items-center px-5 sm:px-8 lg:h-[72px] lg:px-12'>
                    <div className='flex items-center'>
                        <button
                            type='button'
                            onClick={openMenu}
                            aria-expanded={menuOpen}
                            aria-controls='site-menu'
                            className='-ml-2 flex items-center gap-3 p-2'
                        >
                            <Menu className='size-5' strokeWidth={1.25} aria-hidden />
                            <span className='eyebrow max-md:sr-only'>
                                Menu
                            </span>
                        </button>
                    </div>

                    <Link href='/' aria-label='Maison Vaurel, home' className='px-2'>
                        <Wordmark className='text-[1.4rem] lg:text-[1.75rem]' />
                    </Link>

                    <div className='-mr-2 flex items-center justify-end gap-1 sm:gap-3'>
                        <Link href='/appointment' className='flex items-center gap-2.5 p-2'>
                            <CalendarDays className='size-5' strokeWidth={1.25} aria-hidden />
                            <span className='eyebrow max-lg:sr-only'>
                                Book an appointment
                            </span>
                        </Link>

                        <Link href='/bag' aria-label={bagLabel} className='relative flex items-center gap-2.5 p-2'>
                            <ShoppingBag className='size-5' strokeWidth={1.25} aria-hidden />
                            <span aria-hidden className='eyebrow max-lg:hidden'>
                                Bag
                            </span>

                            {ready && count > 0 && (
                                <span aria-hidden className='absolute right-0 top-0.5 flex size-4 items-center justify-center rounded-full bg-foreground text-[0.625rem] font-medium text-background lg:static lg:size-5'>
                                    {count}
                                </span>
                            )}
                        </Link>
                    </div>
                </div>

                <nav aria-label='Collections' className='hidden h-11 items-center justify-center gap-9 lg:flex'>
                    {navigation.map(collection => (
                        <Link
                            key={collection.id}
                            href={`/collections/${collection.slug}`}
                            aria-current={activeSlug === collection.slug ? 'page' : undefined}
                            className='eyebrow link-grow'
                        >
                            <span data-mdlfy-cms={cms('collections', collection.id, 'name')}>
                                {collection.name}
                            </span>
                        </Link>
                    ))}

                    {navigation.length > 0 && <span aria-hidden className='h-3 w-px bg-border' />}

                    {maisonLinks.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            aria-current={router.pathname === link.href ? 'page' : undefined}
                            className='eyebrow link-grow'
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>
            </header>

            <MenuDrawer open={menuOpen} onClose={closeMenu} navigation={navigation} />
        </>
    )
}
